import { Component, ErrorInfo, ReactNode } from 'react';
import { Button, Result, Typography } from 'antd';
import { ReloadOutlined, HomeOutlined } from '@ant-design/icons';
import posthog from 'posthog-js';

const { Paragraph, Text } = Typography;

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

/**
 * Root error boundary component that catches unhandled errors in the React component tree.
 * Reports errors to PostHog analytics and displays a user-friendly error page.
 */
export class RootErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Error caught by boundary:', error, errorInfo);
    
    try {
      if (typeof window !== 'undefined' && posthog && posthog.capture) {
        posthog.capture('app_error', {
          error_message: error.toString(),
          error_stack: error.stack,
          component_stack: errorInfo.componentStack,
          error_boundary: 'root',
          timestamp: new Date().toISOString(),
          url: window.location.href,
          user_agent: navigator.userAgent
        });
      }
    } catch (trackingError) {
      console.error('Failed to track error:', trackingError);
    }

    this.setState({
      errorInfo
    });
  }

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleResetError = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null
    });
  };

  render() {
    if (this.state.hasError) {
      const isDevelopment = process.env.NODE_ENV === 'development';
      
      return (
        <div style={{ 
          minHeight: '100vh', 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'center',
          padding: '20px'
        }}>
          <Result
            status="error"
            title="Something went wrong"
            subTitle="An unexpected error occurred. The error has been reported and we'll look into it."
            extra={[
              <Button 
                type="primary" 
                key="reload" 
                icon={<ReloadOutlined />}
                onClick={this.handleReload}
              >
                Reload Page
              </Button>,
              <Button 
                key="home" 
                icon={<HomeOutlined />}
                onClick={this.handleGoHome}
              >
                Go Home
              </Button>
            ]}
          >
            {isDevelopment && this.state.error && (
              <div style={{ 
                textAlign: 'left', 
                marginTop: '20px',
                padding: '20px',
                backgroundColor: '#f5f5f5',
                borderRadius: '4px',
                maxWidth: '800px',
                margin: '20px auto'
              }}>
                <Paragraph>
                  <Text strong>Error Details (Development Mode):</Text>
                </Paragraph>
                <Paragraph>
                  <Text code>{this.state.error.toString()}</Text>
                </Paragraph>
                {this.state.error.stack && (
                  <>
                    <Paragraph>
                      <Text strong>Stack Trace:</Text>
                    </Paragraph>
                    <pre style={{ 
                      fontSize: '12px', 
                      overflow: 'auto',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px'
                    }}>
                      {this.state.error.stack}
                    </pre>
                  </>
                )}
                {this.state.errorInfo?.componentStack && (
                  <>
                    <Paragraph>
                      <Text strong>Component Stack:</Text>
                    </Paragraph>
                    <pre style={{ 
                      fontSize: '12px', 
                      overflow: 'auto',
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px'
                    }}>
                      {this.state.errorInfo.componentStack}
                    </pre>
                  </>
                )}
              </div>
            )}
          </Result>
        </div>
      );
    }

    return this.props.children;
  }
}